import { contactPage } from "@/mocks/contact";
import GlobalBottomCta from "@/components/feature/GlobalBottomCta";
import PageMeta from "@/components/feature/PageMeta";
import ScrollReveal from "@/components/feature/ScrollReveal";

const pageMeta = {
  title: "聯絡我們｜來加綉綉紅豆餅",
  description: "歡迎聯絡來加綉綉紅豆餅，洽詢產品訂購、冷凍宅配、禮盒團購、企業下午茶、市集合作與加盟創業諮詢。",
  keywords: "來加綉綉聯絡,紅豆餅訂購,紅豆餅諮詢,紅豆餅合作,車輪餅團購,紅豆餅加盟諮詢",
  ogType: "website",
};

export default function ContactPage() {
  const { hero, contactCards, form, brandInfo, map } = contactPage;

  return (
    <main className="pt-16 md:pt-20">
      <PageMeta {...pageMeta} />
      {/* Hero */}
      <section className="relative min-h-[360px] md:min-h-[440px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-redbean-primary/40">
          <img
            src="https://readdy.ai/api/search-image?query=warm%20cozy%20customer%20service%20desk%20with%20taiyaki%20red%20bean%20cake%20bakery%20inviting%20atmosphere%20handwritten%20notes%20and%20warm%20golden%20lighting%20artisan%20Taiwanese%20dessert%20shop%20counter%20welcoming%20customer%20service%20scene%20warm%20amber%20tones&width=1600&height=900&seq=contact-hero-1&orientation=landscape"
            alt="聯絡來加綉綉紅豆餅"
            className="w-full h-full object-cover object-center"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-redbean-coffee/55 via-redbean-coffee/45 to-redbean-coffee/60" />
        </div>
        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 text-center" style={{ maxWidth: "900px" }}>
          <span className="inline-block px-4 py-1.5 bg-redbean-gold text-redbean-coffee text-sm font-semibold rounded-full mb-6">
            {hero.tag}
          </span>
          <h1
            className="font-heading font-black text-redbean-cream mb-5 max-w-3xl mx-auto text-balance"
            style={{ fontSize: "clamp(2rem, 5vw, 2.75rem)", lineHeight: 1.3 }}
          >
            {hero.title}
          </h1>
          <p className="text-redbean-cream/85 max-w-2xl mx-auto" style={{ fontSize: "1.1875rem", lineHeight: 1.75 }}>
            {hero.subtitle}
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16 md:py-20 bg-redbean-cream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          <ScrollReveal>
            <h2
              className="font-heading font-bold text-redbean-coffee text-center mb-10 md:mb-12"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
            >
              {contactCards.sectionTitle}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {contactCards.cards.map((card, idx) => (
              <ScrollReveal key={idx} delay={idx * 100}>
                <div className="bg-white rounded-card p-6 md:p-7 shadow-soft text-center flex flex-col">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-redbean-primary/10 text-redbean-primary">
                    <span className="w-7 h-7 flex items-center justify-center">
                      <i className={`${card.icon} text-2xl`} />
                    </span>
                  </div>
                  <h3
                    className="font-heading font-bold text-redbean-coffee mb-2"
                    style={{ fontSize: "1.25rem", lineHeight: 1.5 }}
                  >
                    {card.title}
                  </h3>
                  <p className="text-redbean-coffee/60 text-[1rem] leading-relaxed mb-5 flex-1">
                    {card.desc}
                  </p>
                  <a
                    href="https://lin.ee/xiNNjjf"
                    rel="nofollow"
                    target="_blank"
                    className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 bg-redbean-primary text-redbean-cream font-semibold rounded-full hover:bg-redbean-coffee transition-colors whitespace-nowrap text-[1.0625rem] min-h-[44px] cursor-pointer"
                  >
                    {card.cta}
                  </a>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 md:py-20 bg-redbean-lightcream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "800px" }}>
          <ScrollReveal>
            <h2
              className="font-heading font-bold text-redbean-coffee text-center mb-10 md:mb-12"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
            >
              {form.sectionTitle}
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <form
              action="https://readdy.ai/api/form/d7u6ps39spvbtqoimkrg"
              method="POST"
              data-readdy-form
              className="bg-white rounded-card p-6 md:p-8 shadow-soft"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-5">
                <div>
                  <label htmlFor="name" className="block text-redbean-coffee font-medium text-[1rem] mb-1.5">
                    {form.fields.name}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-md border border-redbean-caramel/20 bg-redbean-cream/40 text-redbean-coffee text-[1rem] focus:outline-none focus:ring-2 focus:ring-redbean-primary/30 focus:border-redbean-primary transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-redbean-coffee font-medium text-[1rem] mb-1.5">
                    {form.fields.phone}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 rounded-md border border-redbean-caramel/20 bg-redbean-cream/40 text-redbean-coffee text-[1rem] focus:outline-none focus:ring-2 focus:ring-redbean-primary/30 focus:border-redbean-primary transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-redbean-coffee font-medium text-[1rem] mb-1.5">
                    {form.fields.email}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-md border border-redbean-caramel/20 bg-redbean-cream/40 text-redbean-coffee text-[1rem] focus:outline-none focus:ring-2 focus:ring-redbean-primary/30 focus:border-redbean-primary transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="lineId" className="block text-redbean-coffee font-medium text-[1rem] mb-1.5">
                    {form.fields.lineId}
                  </label>
                  <input
                    type="text"
                    id="lineId"
                    name="lineId"
                    className="w-full px-4 py-3 rounded-md border border-redbean-caramel/20 bg-redbean-cream/40 text-redbean-coffee text-[1rem] focus:outline-none focus:ring-2 focus:ring-redbean-primary/30 focus:border-redbean-primary transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-5">
                <div>
                  <label htmlFor="type" className="block text-redbean-coffee font-medium text-[1rem] mb-1.5">
                    {form.fields.type}
                  </label>
                  <select
                    id="type"
                    name="type"
                    required
                    className="w-full px-4 py-3 rounded-md border border-redbean-caramel/20 bg-redbean-cream/40 text-redbean-coffee text-[1rem] focus:outline-none focus:ring-2 focus:ring-redbean-primary/30 focus:border-redbean-primary transition-colors cursor-pointer"
                  >
                    {form.fields.typeOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="preferredTime" className="block text-redbean-coffee font-medium text-[1rem] mb-1.5">
                    {form.fields.preferredTime}
                  </label>
                  <input
                    type="text"
                    id="preferredTime"
                    name="preferredTime"
                    placeholder="例如：平日下午 14:00-17:00"
                    className="w-full px-4 py-3 rounded-md border border-redbean-caramel/20 bg-redbean-cream/40 text-redbean-coffee text-[1rem] focus:outline-none focus:ring-2 focus:ring-redbean-primary/30 focus:border-redbean-primary transition-colors"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-redbean-coffee font-medium text-[1rem] mb-1.5">
                  {form.fields.message}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  required
                  className="w-full px-4 py-3 rounded-md border border-redbean-caramel/20 bg-redbean-cream/40 text-redbean-coffee text-[1rem] focus:outline-none focus:ring-2 focus:ring-redbean-primary/30 focus:border-redbean-primary transition-colors resize-none"
                  placeholder="請簡述你的需求..."
                />
                <p className="text-redbean-coffee/40 text-xs mt-1">最多 500 字</p>
              </div>

              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-3.5 bg-redbean-primary text-redbean-cream font-semibold rounded-full hover:bg-redbean-coffee transition-colors whitespace-nowrap text-[1.0625rem] min-h-[44px] cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-send-plane-line" />
                </span>
                {form.fields.submit}
              </button>
            </form>
          </ScrollReveal>

          <ScrollReveal delay={150}>
            <p className="text-center text-redbean-coffee/50 text-[1rem] leading-relaxed mt-6">
              {form.note}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Brand Info */}
      <section className="py-16 md:py-20 bg-redbean-cream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          <ScrollReveal>
            <h2
              className="font-heading font-bold text-redbean-coffee text-center mb-10 md:mb-12"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
            >
              {brandInfo.sectionTitle}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {brandInfo.items.map((item, idx) => (
              <ScrollReveal key={idx} delay={idx * 80}>
                <div className="flex items-start gap-3 bg-white rounded-card p-5 shadow-soft">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-redbean-primary/10 text-redbean-primary">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${item.icon} text-lg`} />
                    </span>
                  </div>
                  <div>
                    <p className="text-redbean-coffee/50 text-sm mb-0.5">{item.label}</p>
                    <p className="text-redbean-coffee font-medium text-[1.0625rem]">{item.value}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={200}>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {brandInfo.socials.map((social, idx) => (
                <a
                  key={idx}
                  href={social.url}
                  rel="nofollow"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white rounded-full shadow-soft text-redbean-coffee text-sm font-medium hover:bg-redbean-primary hover:text-redbean-cream transition-colors whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={social.icon} />
                  </span>
                  {social.label}
                </a>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Map */}
      <section className="py-16 md:py-20 bg-redbean-lightcream">
        <div className="mx-auto px-4 md:px-6 lg:px-8 text-center" style={{ maxWidth: "800px" }}>
          <ScrollReveal>
            <div className="bg-white rounded-card p-8 md:p-12 shadow-soft">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-redbean-gold/20 text-redbean-caramel">
                <span className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-map-pin-line text-2xl" />
                </span>
              </div>
              <h3
                className="font-heading font-bold text-redbean-coffee mb-2"
                style={{ fontSize: "1.25rem", lineHeight: 1.5 }}
              >
                門市地圖
              </h3>
              <p className="text-redbean-coffee/60 text-[1rem] leading-relaxed">
                {map.noAddress}
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <GlobalBottomCta />
    </main>
  );
}